import React, { Suspense } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Center, Loading } from '@cotera/client/app/components';
import {
  ModalPageView,
  useModalManager,
  useOptionalModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import { Assert } from '@cotera/utilities';
import { Tool } from '@cotera/client/app/hooks/use-tools';
import { useProviderTools } from '@cotera/client/app/hooks/use-available-tools';
import { ToolConnections, ToolSettingsModalPageRoot } from './tool-connections';
import { ProviderDetailPage } from './provider-detail';
import { getProviderFromToolKey } from './utils';
import { SLACK_PROVIDER_KEY, SLACKSEARCH_PROVIDER_KEY } from './slack-provider';
import { ToolKey } from '@cotera/sdk/core';
import { CocoEvent, useAnalytics } from '@cotera/client/app/etc';

/**
 * The catalog is a two-pane layout, so it needs noticeably more width than a
 * single-column list did — capped so the panes don't stretch on wide monitors.
 */
const TOOLS_MODAL_SIZE = {
  custom:
    'w-[92vw] h-[80dvh] md:w-[76vw] md:max-w-[1180px] md:h-[82vh] rounded-lg max-h-[86dvh]',
} as const;

/**
 * `ModalPageView` caps its content at `max-w-5xl`, which would leave dead space
 * beside the right pane on wide screens.
 */
const TOOLS_MODAL_PAGE_CLASS = 'px-6 py-5 flex flex-col h-full max-w-none';

// Hook to fetch tool credential info
export const useToolCredentialInfo = (toolKey: string) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  return useSuspenseQuery({
    queryKey: queryKeys.toolsV2.credentialInfo({ orgId, toolKey }),
    queryFn: async () => {
      const result = await client.toolsV2.getToolCredentialInfo({
        toolKey: ToolKey.fromString(toolKey).toString(),
      });
      return Assert.assertOk(result);
    },
  });
};

// Hook to fetch provider accounts
export const useProviderAccounts = (provider: string) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  return useSuspenseQuery({
    queryKey: queryKeys.toolsV2.providerAccounts({
      orgId,
      provider,
    }),
    queryFn: async () => {
      const primary = Assert.assertOk(
        await client.toolsV2.listProviderAccounts({
          provider,
        })
      );
      if (provider !== SLACK_PROVIDER_KEY) {
        return primary;
      }
      const legacySearch = Assert.assertOk(
        await client.toolsV2.listProviderAccounts({
          provider: SLACKSEARCH_PROVIDER_KEY,
        })
      );
      return {
        provider,
        accounts: [...primary.accounts, ...legacySearch.accounts],
      };
    },
  });
};

// Hook to delete a provider account
export const useDeleteProviderAccount = (provider: string) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  return useMutation({
    mutationFn: async (accountId: string) => {
      const result = await client.toolsV2.deleteProviderAccount({ accountId });
      return Assert.assertOk(result);
    },
    onSuccess: () => {
      // Broadcast the deletion; the tool-credential-updated query-sync watchable
      // refetches the affected tool caches (tools list, provider accounts, etc.).
      emitToolCredentialUpdated(orgId, provider);
    },
  });
};

/**
 * Hook to open the tools modal directly from anywhere in the app.
 * This provides a streamlined way to access the tool browser without
 * going through the settings modal first.
 */
export const useOpenToolsModal = () => {
  // Optional, not required: this is now called from the chat composer, which is
  // a shared component that also renders in tests and embeds with no modal
  // provider above it. The store hook throws there; this one returns null.
  const modalManager = useOptionalModalManager();
  const analytics = useAnalytics();

  return () => {
    if (modalManager === null) {
      return;
    }
    analytics.track(CocoEvent.ToolConnectionsOpened, {});
    modalManager.push({
      size: TOOLS_MODAL_SIZE,
      view: () => (
        <ModalPageView
          className={TOOLS_MODAL_PAGE_CLASS}
          title="Tools"
          subtitle="Browse and connect tools for your agents"
        >
          <Suspense
            fallback={
              <Center>
                <Loading.Dots />
              </Center>
            }
          >
            <ToolConnections onSelect={async () => {}} />
          </Suspense>
        </ModalPageView>
      ),
    });
  };
};

/**
 * Full ProviderDetailPage loaded lazily via listToolsForProvider.
 * Shows all tools, credential form, etc. - same as existing modal.
 */
const ProviderDetailByProviderKey: React.FC<{
  providerKey: string;
  expandedToolKey?: string;
}> = ({ providerKey, expandedToolKey }) => {
  const provider = useProviderTools(providerKey);
  if (provider === null) {
    return null;
  }
  return (
    <ProviderDetailPage provider={provider} expandedToolKey={expandedToolKey} />
  );
};

/**
 * Hook to open the tool connections modal for a specific provider.
 * Shows full ProviderDetailPage (credentials, accounts, etc.) for that provider.
 */
export const useOpenProviderModal = () => {
  const modalManager = useModalManager((s) => s.actions);

  return React.useCallback(
    (providerKey: string) => {
      modalManager.push({
        size: TOOLS_MODAL_SIZE,
        view: () => (
          <Suspense
            fallback={
              <Center>
                <Loading.Dots />
              </Center>
            }
          >
            <ProviderDetailByProviderKey providerKey={providerKey} />
          </Suspense>
        ),
      });
    },
    [modalManager]
  );
};

/**
 * Hook to open the tool modal for a specific tool by key.
 * Shows full ProviderDetailPage (all tools, credentials, etc.) loaded lazily
 * via listToolsForProvider - only fetches that provider's tools.
 */
export const useOpenToolModal = () => {
  const modalManager = useModalManager((s) => s.actions);

  return React.useCallback(
    (toolKey: string) => {
      const providerKey = getProviderFromToolKey(toolKey);
      modalManager.push({
        size: TOOLS_MODAL_SIZE,
        view: () => (
          <Suspense
            fallback={
              <Center>
                <Loading.Dots />
              </Center>
            }
          >
            <ProviderDetailByProviderKey
              providerKey={providerKey}
              expandedToolKey={toolKey}
            />
          </Suspense>
        ),
      });
    },
    [modalManager]
  );
};

export const useShowToolSettingsModal = (
  onSelect: (tool: Tool & { isExisting?: boolean }) => Promise<void>
) => {
  const manager = useModalManager((s) => s.actions);

  return () => {
    manager.push({
      size: TOOLS_MODAL_SIZE,
      view: () => (
        <ToolSettingsModalPageRoot
          onSelect={async (tool) => {
            manager.close();
            await onSelect(tool);
          }}
        />
      ),
    });
  };
};
